import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import {
  ChangePasswordWithTokenRequest,
  ChangePasswordRequest,
  TokenValidateRequest
} from '../Types/Requests/UserRequest'
import {
  ChangePasswordWithTokenResponse,
  VerifyCpfResponse,
  VerifyEmailResponse,
  UserDataResponse
} from '../Types/Responses/UserResponse'
import { ListCompanyUserResponse } from '../Types/Responses/CompanyResponses'

export const STORAGE_TOKEN_KEY = ``

export default class UserService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/users'
  }

  async getAllUsers(searchTerm?: string): Promise<ListCompanyUserResponse> {
    let url = this.getCompletePath('/')
    if (searchTerm && searchTerm.trim()) {
      url += `?searchTerm=${encodeURIComponent(searchTerm)}`
    }

    try {
      const response = await request.get<ListCompanyUserResponse>(url)

      return {
        data: response.data.data,
        count: response.data.count
      }
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os usuários.')
    }
  }

  async validateToken(payload: TokenValidateRequest): Promise<void> {
    try {
      await request.post<void>(this.getCompletePath('/validate-token'), payload)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível validar o link de recuperação de senha.')
    }
  }

  async forgotPasswordRequest(email: string): Promise<void> {
    try {
      await request.post<void>(this.getCompletePath('/forgot-password'), { email })
    } catch (e) {
      throw this.handleError(e, 'Não foi possível enviar o link de recuperação de senha.')
    }
  }

  async changePassword(
    payload: ChangePasswordWithTokenRequest
  ): Promise<ChangePasswordWithTokenResponse> {
    try {
      const { data } = await request.post<ChangePasswordWithTokenResponse>(
        this.getCompletePath('/change-password-with-token'),
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível alterar a senha.')
    }
  }

  async changeCompanyUserPassword(
    payload: ChangePasswordRequest
  ): Promise<ChangePasswordWithTokenResponse> {
    try {
      const { data } = await request.post<ChangePasswordWithTokenResponse>(
        this.getCompletePath('/change-password-with-bearer'),
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível alterar a senha.')
    }
  }

  async verifyEmail(email: string): Promise<VerifyEmailResponse> {
    try {
      const { data } = await request.get<VerifyEmailResponse>(
        this.getCompletePath('/verify-email'),
        { email }
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível verificar o e-mail.')
    }
  }

  async verifyCpf(cpf: string): Promise<VerifyCpfResponse> {
    try {
      const { data } = await request.get<VerifyCpfResponse>(this.getCompletePath('/verify-cpf'), {
        cpf
      })
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível verificar o CPF.')
    }
  }

  async getUserData(): Promise<UserDataResponse> {
    try {
      const { data } = await request.get<UserDataResponse>(this.getCompletePath('/user-data'))
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os seus dados.')
    }
  }

  async updateUserData(updatedData: Partial<UserDataResponse>): Promise<UserDataResponse> {
    try {
      const { data } = await request.put<UserDataResponse>(
        this.getCompletePath('/update-user'),
        updatedData
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar os seus dados.')
    }
  }

  async toggleUserStatus(userId: number, isActive: boolean): Promise<void> {
    try {
      await request.put<void>(this.getCompletePath(`/toggle-status?userId=${userId}`), {
        isActive: isActive
      })
    } catch (e) {
      throw this.handleError(e, 'Não foi possível alterar o status do usuário.')
    }
  }

  async updateAvatar(file: File): Promise<{ avatarUrl: string }> {
    const formData = new FormData()
    formData.append('file', file)
    try {
      const { data } = await request.put<{ avatarUrl: string }>(
        this.getCompletePath('/avatar'),
        formData,
        { 'Content-Type': 'multipart/form-data' }
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar a foto de perfil.')
    }
  }
}
